import store from '../redux/store';
import {
  restoreGridHistorySnapshot,
  setSelectedMeshesForGrouping,
  setPushIntersectedObject,
  selected_object,
} from '../redux/Slice/gridSlice';
import { setSelectedLightId } from '../redux/Slice/globalStates';
import { setParticlesHistory } from '../redux/Slice/particleSlice';
import { setAnimationObjectsHistory } from '../redux/Slice/animationSlice';
import { restoreEnvironmentHistory } from '../redux/Slice/environmentSlice';
import { restorePhysicsHistory } from '../redux/Slice/physicsSlice';
import { refreshAnimationTransformAnchors } from './ThreeJsUtils/syncObjectTransform';
import { syncSceneAfterGridHistoryRestore } from './gridHistorySceneSync';
import { suppressGridHistory, beginGridHistoryRestore, endGridHistoryRestore } from './gridHistorySuppress';
import { cancelPendingGridHistoryCommit } from './gridHistoryCommit';
import {
  captureSubtreeMaterialOpacity,
  restoreSubtreeMaterialOpacity,
} from './ThreeJsUtils/materialOpacitySnapshot';
import { safeHistoryClone, safeHistoryCloneOrEmpty } from './safeHistoryClone';
import { buildTextReduxAssetRecord } from './textAssetUtils';
import { collectAssetLookupIds, mergeAssetsDataWithScene } from './gridHistoryAssetsSync';
import { clampSelectionForChildMode, isGlbInnerMesh } from '../components/gridpagefunction/MeshExtractation';
import { resolveParticleAnimationRoot } from './particles/particleAnimationUtils';
import {
  isLiveSceneObject3D,
  resolveLiveSceneObject,
} from './ThreeJsUtils/assetVisibilityUtils';
import { isSceneLight } from './ThreeJsUtils/lightHelperUtils';
import {
  getCameraStableId,
  isSceneCamera,
  isViewportCamera,
  resolveSceneCameraObject,
} from './ThreeJsUtils/sceneCameraUtils';

const SKIP_OBJECT_NAMES = new Set([
  'gridHelper',
  'transformControls',
  'hdriBackgroundSphere',
  'mrCenterCube',
  'mrCenterCubeEdges',
]);

let gridSceneRef = null;
let gridEditorContextRef = null;
const detachedObjectPool = new Map();

export function setGridHistoryScene(scene) {
  gridSceneRef = scene;
}

export function setGridHistoryEditorContext(context) {
  gridEditorContextRef = context || null;
}

export function getGridHistoryEditorContext() {
  return gridEditorContextRef;
}

export function getDetachedSceneObjects() {
  const unique = new Set();
  detachedObjectPool.forEach((obj) => {
    if (obj?.isObject3D) unique.add(obj);
  });
  return [...unique];
}

export function findDetachedSceneObjectByRecord(record) {
  if (!record) return null;
  const lookupIds = new Set(collectAssetLookupIds(record));

  for (const candidate of getDetachedSceneObjects()) {
    const candidateIds = collectAssetLookupIds(candidate);
    if (candidateIds.some((id) => lookupIds.has(id))) {
      return candidate;
    }
  }

  return null;
}

export function getGridHistoryScene() {
  return gridSceneRef;
}

function getSelectedObjectId(selectObject) {
  if (!selectObject) return null;
  if (typeof selectObject === 'string') return selectObject;
  return (
    selectObject.name ||
    selectObject._id ||
    selectObject.id ||
    selectObject.url ||
    selectObject.temp_file_name ||
    null
  );
}

function shouldTrackObject(obj) {
  if (!obj || obj.type === 'Scene') return false;
  if (obj === gridSceneRef) return false;
  if (SKIP_OBJECT_NAMES.has(obj.name)) return false;
  if (obj.userData?.isHdriBackground) return false;
  if (obj.userData?.isEditorLightIcon) return false;
  if (obj.userData?.isParticleOutlineProxy) return false;
  if (obj.name === '__particleOutlineProxy') return false;
  // Camera helpers/pick-points are ephemeral UI — driven by helperVisible, not history.
  if (obj.type === 'CameraHelper') return false;
  if (obj.name?.endsWith('Helper') && obj.isCamera === false && obj.type === 'CameraHelper') {
    return false;
  }
  if (obj.name?.endsWith('Point') && !obj.deleteType && !obj.isLight) {
    // Scene-camera pick proxies (DemoCameraPoint, etc.)
    const camId = obj.name.slice(0, -'Point'.length);
    if (camId && gridSceneRef?.getObjectByName(camId)?.isCamera) return false;
  }
  // Asset / entity roots only — never every GLB leaf mesh (was freezing undo capture).
  if (obj.deleteType) return true;
  // Built-in shapes (Cube, Sphere, Plane, Capsule, …)
  if (
    obj.isShapes ||
    obj.userData?.isShapes ||
    obj.glb_type === 'shapes' ||
    obj.userData?.glb_type === 'shapes' ||
    obj.userData?.type === 'shape'
  ) {
    return true;
  }
  if (obj.isCamera || obj.isLight) return true;
  if (obj.objType) return true;
  // Light helpers only (not camera helpers — already excluded above)
  if (obj.type?.endsWith('Helper') && obj.type !== 'CameraHelper') return true;
  return false;
}

/** Live Three.js transform anchors — never restore as plain JSON objects. */
const ANIMATION_TRANSFORM_USERDATA_KEYS = [
  'originalPosition',
  'originalRotation',
  'originalScale',
  'framePosition',
  'frameRotation',
  'frameScale',
  'prevRotation',
  'prevPosition',
  'prevScale',
];

function cloneUserData(userData) {
  return safeHistoryCloneOrEmpty(userData);
}

function cloneUserDataForRestore(userData) {
  const cloned = safeHistoryCloneOrEmpty(userData);
  ANIMATION_TRANSFORM_USERDATA_KEYS.forEach((key) => {
    delete cloned[key];
  });
  return cloned;
}

function serializeAssetRecord(item) {
  if (!item) return item;
  if (item.isObject3D || item.isMesh || item.isGroup) {
    const textRecord =
      item.deleteType === 'text' ? buildTextReduxAssetRecord(item) : null;
    return safeHistoryCloneOrEmpty(
      textRecord || {
      _id: item._id ?? item.id ?? item.name ?? item.url,
      id: item.id ?? item._id ?? item.name ?? item.url,
      name: item.name,
      url: item.url,
      temp_file_name: item.temp_file_name,
      file_name: item.file_name ?? item.userData?.file_name,
      asset_file_name: item.asset_file_name,
      deleteType: item.deleteType,
      objType: item.objType,
      // Shape primitives (Cube, Sphere, Plane, …)
      glb_type: item.glb_type ?? item.userData?.glb_type,
      mesh_code: item.mesh_code ?? item.userData?.mesh_code,
      shape_type: item.shape_type ?? item.userData?.shape_type,
      isShapes:
        item.isShapes === true ||
        item.userData?.isShapes === true ||
        item.glb_type === 'shapes' ||
        item.userData?.glb_type === 'shapes',
      position_x: item.position?.x,
      position_y: item.position?.y,
      position_z: item.position?.z,
      rotation_x: item.rotation?.x,
      rotation_y: item.rotation?.y,
      rotation_z: item.rotation?.z,
      scale_x: item.scale?.x,
      scale_y: item.scale?.y,
      scale_z: item.scale?.z,
      visible: item.visible,
      events: item.events,
      material_settings: item.userData?.material_settings,
      text_material: item.userData?.text_material,
      userData: safeHistoryCloneOrEmpty(item.userData),
    }
    );
  }
  return safeHistoryCloneOrEmpty(item);
}

function captureSceneGraph(scene) {
  if (!scene) return { nodes: [], rootChildUuids: [] };

  const nodes = [];
  const visit = (obj) => {
    if (!shouldTrackObject(obj)) {
      // Still walk children so nested asset roots (parented hierarchy) are found.
      obj?.children?.forEach(visit);
      return;
    }
    const node = {
      uuid: obj.uuid,
      parentUuid: obj.parent?.uuid || null,
      parentIsScene: obj.parent === scene,
      position: obj.position.toArray(),
      rotation: obj.rotation.toArray(),
      scale: obj.scale.toArray(),
      quaternion: obj.quaternion.toArray(),
      visible: obj.visible,
      name: obj.name,
      deleteType: obj.deleteType,
      glb_type: obj.glb_type ?? obj.userData?.glb_type,
      mesh_code: obj.mesh_code ?? obj.userData?.mesh_code,
      isShapes:
        obj.isShapes === true ||
        obj.userData?.isShapes === true ||
        obj.glb_type === 'shapes',
      events: obj.events ? cloneUserData(obj.events) : undefined,
      Events: obj.Events ? cloneUserData(obj.Events) : undefined,
      userData: cloneUserData(obj.userData),
      // One opacity walk per tracked root — not per nested node.
      materialOpacitySnapshot: captureSubtreeMaterialOpacity(obj),
    };
    if (obj.isPerspectiveCamera) {
      node.cameraProps = {
        fov: obj.fov,
        near: obj.near,
        far: obj.far,
        zoom: obj.zoom,
      };
    } else if (obj.isOrthographicCamera) {
      node.cameraProps = {
        near: obj.near,
        far: obj.far,
        zoom: obj.zoom,
      };
    }
    nodes.push(node);
    obj.children.forEach(visit);
  };

  scene.children.forEach(visit);

  return {
    nodes,
    rootChildUuids: scene.children
      .filter(shouldTrackObject)
      .map((child) => child.uuid),
  };
}

export function captureGridHistoryState(scene = gridSceneRef) {
  const { assets_object, particles, animation, environment, physics } =
    store.getState();

  return {
    redux: {
      sceneMeshes: [...(assets_object.sceneMeshes || [])],
      sceneHotspots: [...(assets_object.sceneHotspots || [])],
      sceneImages: [...(assets_object.sceneImages || [])],
      sceneVideos: [...(assets_object.sceneVideos || [])],
      sceneAudios: [...(assets_object.sceneAudios || [])],
      sceneText: [...(assets_object.sceneText || [])],
      sceneElements: [...(assets_object.sceneElements || [])],
      allIntersectedObjects: (assets_object.allIntersectedObjects || []).filter(
        (obj) => {
          if (isGlbInnerMesh(scene, obj)) return false;
          const particleRoot = resolveParticleAnimationRoot(obj);
          if (particleRoot && particleRoot !== obj) return false;
          return true;
        }
      ),
      assets_data: mergeAssetsDataWithScene(
        assets_object.assets_data,
        scene
      ),
      light_data: safeHistoryClone(assets_object.light_data || []) ?? [],
      camera_properties:
        safeHistoryClone(assets_object.camera_properties || []) ?? [],
      events: safeHistoryClone(assets_object.events || []) ?? [],
      selectedObjectId: (() => {
        const current = assets_object.select_object;
        if (!current) return null;
        if (current.isObject3D && scene) {
          return getSelectedObjectId(clampHistorySelection(scene, current));
        }
        return getSelectedObjectId(current);
      })(),
    },
    particles: safeHistoryClone(particles?.particles || []) ?? [],
    animationObjects: safeHistoryClone(animation?.animationObjects || []) ?? [],
    environment: safeHistoryCloneOrEmpty(environment?.environmentData),
    physics: {
      physicsEnabled: physics?.physicsEnabled ?? false,
      worldSettings: safeHistoryCloneOrEmpty(physics?.worldSettings),
      physicsObjects: safeHistoryClone(physics?.physicsObjects || []) ?? [],
    },
    sceneGraph: captureSceneGraph(scene),
  };
}

function registerDetachedObject(obj) {
  if (!obj?.uuid) return;
  detachedObjectPool.set(obj.uuid, obj);
  obj.traverse?.((child) => {
    if (child?.uuid) detachedObjectPool.set(child.uuid, child);
  });
}

export function registerDetachedObjectsForUndo(...objects) {
  objects.filter(Boolean).forEach(registerDetachedObject);
}

function buildLiveObjectMap(scene) {
  const map = new Map();
  if (!scene) return map;
  scene.traverse((obj) => {
    if (obj?.uuid) map.set(obj.uuid, obj);
  });
  detachedObjectPool.forEach((obj, uuid) => {
    if (!map.has(uuid)) map.set(uuid, obj);
  });
  return map;
}

function rememberLiveObject(liveMap, obj, aliasUuid = null) {
  if (!obj?.uuid || !liveMap) return obj;
  liveMap.set(obj.uuid, obj);
  if (aliasUuid) liveMap.set(aliasUuid, obj);
  return obj;
}

function resolveLiveObjectForGraphNode(node, liveMap, scene) {
  if (!node) return null;

  let obj = liveMap.get(node.uuid);
  if (obj) return obj;

  if (node.name) {
    const byName = scene.getObjectByName(node.name);
    if (byName) return rememberLiveObject(liveMap, byName, node.uuid);
  }

  for (const candidate of detachedObjectPool.values()) {
    if (candidate.uuid === node.uuid) {
      return rememberLiveObject(liveMap, candidate, node.uuid);
    }
    if (
      node.name &&
      (candidate.name === node.name ||
        candidate._id === node.name ||
        candidate.url === node.name ||
        candidate.temp_file_name === node.name)
    ) {
      return rememberLiveObject(liveMap, candidate, node.uuid);
    }
  }

  return null;
}

function applyGraphNodeState(node, obj, scene, liveMap) {
  const targetParent = node.parentIsScene
    ? scene
    : liveMap.get(node.parentUuid);
  if (targetParent && obj.parent !== targetParent) {
    obj.parent?.remove(obj);
    targetParent.add(obj);
  }

  obj.position.fromArray(node.position);
  obj.rotation.fromArray(node.rotation);
  obj.scale.fromArray(node.scale);
  if (node.quaternion) obj.quaternion.fromArray(node.quaternion);
  obj.visible = node.visible !== false;
  if (node.events) obj.events = cloneUserData(node.events);
  if (node.Events) obj.Events = cloneUserData(node.Events);
  if (node.glb_type != null) obj.glb_type = node.glb_type;
  if (node.mesh_code != null) obj.mesh_code = node.mesh_code;
  if (node.isShapes) obj.isShapes = true;
  if (node.cameraProps && obj.isCamera) {
    const cp = node.cameraProps;
    if (cp.fov != null && obj.isPerspectiveCamera) obj.fov = cp.fov;
    if (cp.near != null) obj.near = cp.near;
    if (cp.far != null) obj.far = cp.far;
    if (cp.zoom != null) obj.zoom = cp.zoom;
    obj.updateProjectionMatrix?.();
  }
  if (node.userData) {
    const restoredUserData = cloneUserDataForRestore(node.userData);
    try {
      const prev = obj.userData || {};
      const next = { ...prev, ...restoredUserData };
      ANIMATION_TRANSFORM_USERDATA_KEYS.forEach((key) => {
        delete next[key];
      });
      obj.userData = next;
    } catch {
      obj.userData = restoredUserData;
    }
  }

  if (node.materialOpacitySnapshot) {
    restoreSubtreeMaterialOpacity(obj, node.materialOpacitySnapshot);
  }

  refreshAnimationTransformAnchors(obj);
  obj.updateMatrix?.();
  obj.updateMatrixWorld?.(true);
}

function reattachRegisteredRootsFromPools(scene, graph, liveMap) {
  const rootUuidSet = new Set(graph?.rootChildUuids || []);
  const state = store.getState().assets_object;
  const candidates = new Set();

  [
    ...(state.sceneMeshes || []),
    ...(state.sceneImages || []),
    ...(state.sceneVideos || []),
    ...(state.sceneAudios || []),
    ...(state.sceneText || []),
    ...(state.sceneHotspots || []),
    ...(state.allIntersectedObjects || []),
  ].forEach((obj) => {
    if (obj?.isObject3D) candidates.add(obj);
  });

  detachedObjectPool.forEach((obj) => {
    if (obj?.isObject3D) candidates.add(obj);
  });

  const reattached = [];
  candidates.forEach((obj) => {
    if (!shouldTrackObject(obj)) return;
    if (rootUuidSet.size > 0 && !rootUuidSet.has(obj.uuid)) return;
    if (obj.parent === scene) return;

    rememberLiveObject(liveMap, obj);
    obj.parent?.remove?.(obj);
    scene.add(obj);
    reattached.push(obj);
  });

  if (!reattached.length) return;

  const endSuppress = suppressGridHistory();
  try {
    reattached.forEach((obj) => {
      store.dispatch(setPushIntersectedObject(obj));
    });
  } finally {
    endSuppress();
  }
}

function restoreSceneGraph(scene, graph) {
  if (!scene || !graph) return;

  const liveMap = buildLiveObjectMap(scene);
  const targetRootUuids = new Set(graph.rootChildUuids || []);

  [...scene.children].forEach((child) => {
    if (shouldTrackObject(child) && !targetRootUuids.has(child.uuid)) {
      if (child.deleteType === 'mesh' || child.objType === '3d') {
        try {
          child.traverse?.((node) => {
            node.userData?.mixer?.stopAllAction?.();
          });
        } catch (_) {}
      }
      scene.remove(child);
      registerDetachedObject(child);
    }
  });

  (graph.nodes || []).forEach((node) => {
    const obj = resolveLiveObjectForGraphNode(node, liveMap, scene);
    if (!obj) return;
    applyGraphNodeState(node, obj, scene, liveMap);
  });

  (graph.rootChildUuids || []).forEach((uuid) => {
    const obj =
      liveMap.get(uuid) ||
      resolveLiveObjectForGraphNode({ uuid, name: null }, liveMap, scene);
    if (obj && obj.parent !== scene) {
      obj.parent?.remove?.(obj);
      scene.add(obj);
      rememberLiveObject(liveMap, obj, uuid);
    }
  });

  reattachRegisteredRootsFromPools(scene, graph, liveMap);
  pruneDetachedPoolToGraph(graph);
}

function pruneDetachedPoolToGraph(graph) {
  if (!graph) return;

  const allowed = new Set(graph.rootChildUuids || []);
  (graph.nodes || []).forEach((node) => {
    if (node?.uuid) allowed.add(node.uuid);
  });

  [...detachedObjectPool.keys()].forEach((uuid) => {
    if (!allowed.has(uuid)) {
      detachedObjectPool.delete(uuid);
    }
  });
}

function stableIdFromRecord(item) {
  if (!item) return null;
  return (
    item.id ||
    item._id ||
    item.name ||
    item.url ||
    item.temp_file_name ||
    null
  );
}

function compareRecordList(beforeArr, afterArr, changed, getId = stableIdFromRecord) {
  const beforeMap = new Map();
  const afterMap = new Map();
  (beforeArr || []).forEach((item) => {
    const id = getId(item);
    if (id) beforeMap.set(id, item);
  });
  (afterArr || []).forEach((item) => {
    const id = getId(item);
    if (id) afterMap.set(id, item);
  });

  const ids = new Set([...beforeMap.keys(), ...afterMap.keys()]);
  ids.forEach((id) => {
    const beforeItem = beforeMap.get(id);
    const afterItem = afterMap.get(id);
    if (
      !beforeItem ||
      !afterItem ||
      JSON.stringify(beforeItem) !== JSON.stringify(afterItem)
    ) {
      changed.add(id);
    }
  });
}

function compareAssetsData(before, after, changed) {
  const types = ['mesh', 'image', 'video', 'audio', 'text', 'hotspot', 'empty'];
  types.forEach((type) => {
    compareRecordList(before?.[type], after?.[type], changed);
  });
}

function sceneNodeSnapshotChanged(a, b) {
  const pick = (node) => ({
    position: node.position,
    rotation: node.rotation,
    scale: node.scale,
    quaternion: node.quaternion,
    visible: node.visible,
    userData: node.userData,
    glb_type: node.glb_type,
    mesh_code: node.mesh_code,
    materialOpacitySnapshot: node.materialOpacitySnapshot,
    cameraProps: node.cameraProps,
  });
  try {
    return JSON.stringify(pick(a)) !== JSON.stringify(pick(b));
  } catch {
    return true;
  }
}

function compareSceneGraphChanges(beforeGraph, afterGraph, changed) {
  const beforeNodes = new Map(
    (beforeGraph?.nodes || []).map((node) => [node.uuid, node])
  );
  const afterNodes = new Map(
    (afterGraph?.nodes || []).map((node) => [node.uuid, node])
  );
  const uuids = new Set([...beforeNodes.keys(), ...afterNodes.keys()]);

  uuids.forEach((uuid) => {
    const beforeNode = beforeNodes.get(uuid);
    const afterNode = afterNodes.get(uuid);
    if (!beforeNode && afterNode?.name) {
      changed.add(afterNode.name);
    } else if (beforeNode && !afterNode && beforeNode?.name) {
      changed.add(beforeNode.name);
    } else if (
      beforeNode &&
      afterNode &&
      sceneNodeSnapshotChanged(beforeNode, afterNode)
    ) {
      changed.add(afterNode.name || beforeNode.name);
    }
  });
}

function collectChangedIds(beforeSnapshot, afterSnapshot) {
  const changed = new Set();
  if (!beforeSnapshot || !afterSnapshot) return [];

  compareRecordList(
    beforeSnapshot?.redux?.light_data,
    afterSnapshot?.redux?.light_data,
    changed
  );
  compareRecordList(
    beforeSnapshot?.redux?.camera_properties,
    afterSnapshot?.redux?.camera_properties,
    changed
  );
  compareAssetsData(
    beforeSnapshot?.redux?.assets_data,
    afterSnapshot?.redux?.assets_data,
    changed
  );
  compareRecordList(beforeSnapshot?.particles, afterSnapshot?.particles, changed);
  compareSceneGraphChanges(
    beforeSnapshot?.sceneGraph,
    afterSnapshot?.sceneGraph,
    changed
  );

  return [...changed];
}

function isSceneCameraId(id, reduxSnapshot) {
  if (!id) return false;
  return (reduxSnapshot?.camera_properties || []).some((cam) => {
    const stableId = getCameraStableId(cam);
    return stableId === id || cam.name === id || cam._id === id || cam.id === id;
  });
}

function pickHistorySelectionId(changedIds, preferredId, reduxSnapshot) {
  const ids = (changedIds || []).filter(Boolean);
  if (ids.length === 0) {
    return preferredId || null;
  }
  if (ids.length === 1) {
    return ids[0];
  }
  if (preferredId && ids.includes(preferredId)) {
    return preferredId;
  }

  const nonCameraIds = ids.filter(
    (id) => !isSceneCameraId(id, reduxSnapshot)
  );
  if (nonCameraIds.length > 0) {
    return nonCameraIds[0];
  }

  return ids[0];
}

/**
 * After undo/redo restore, pick the live object that was actually changed
 * (not the viewport camera by fallback).
 */
export function resolveHistorySelectionTarget(
  beforeSnapshot,
  afterSnapshot,
  scene,
  mode
) {
  if (!scene) return null;

  const targetSnapshot = mode === 'undo' ? beforeSnapshot : afterSnapshot;
  const changedIds = collectChangedIds(beforeSnapshot, afterSnapshot);
  const preferredId = targetSnapshot?.redux?.selectedObjectId || null;
  const selectionId = pickHistorySelectionId(
    changedIds,
    preferredId,
    targetSnapshot?.redux
  );

  if (!selectionId) return null;
  return resolveSelectObject(scene, selectionId, targetSnapshot?.redux);
}

function clampHistorySelection(scene, object) {
  if (!object || !scene) return object;
  const particleRoot = resolveParticleAnimationRoot(object);
  if (particleRoot) return particleRoot;
  return clampSelectionForChildMode(scene, object, null);
}

function resolveSelectObject(scene, selectedObjectId, reduxSnapshot) {
  if (!selectedObjectId || !scene) return null;

  let fromScene = scene.getObjectByName(selectedObjectId);
  if (!fromScene) {
    scene.traverse((obj) => {
      if (fromScene) return;
      if (
        obj.name === selectedObjectId ||
        obj.uuid === selectedObjectId ||
        obj._id === selectedObjectId ||
        obj.userData?._id === selectedObjectId ||
        obj.userData?.id === selectedObjectId
      ) {
        fromScene = obj;
      }
    });
  }

  if (fromScene) {
    if (isViewportCamera(fromScene)) return null;
    return clampHistorySelection(scene, fromScene);
  }

  const lightRecord = (reduxSnapshot?.light_data || []).find(
    (light) =>
      light.id === selectedObjectId ||
      light.name === selectedObjectId ||
      light._id === selectedObjectId ||
      light.temp_file_name === selectedObjectId
  );
  if (lightRecord) {
    const lightObj = scene.getObjectByName(lightRecord.id || lightRecord.name);
    if (isSceneLight(lightObj)) return lightObj;
  }

  const cameraRecord = (reduxSnapshot?.camera_properties || []).find((cam) => {
    const stableId = getCameraStableId(cam);
    return (
      stableId === selectedObjectId ||
      cam.name === selectedObjectId ||
      cam._id === selectedObjectId ||
      cam.id === selectedObjectId
    );
  });
  if (cameraRecord) {
    const cameraObj = resolveSceneCameraObject(scene, cameraRecord);
    if (isSceneCamera(cameraObj)) return cameraObj;
  }

  const pools = [
    ...(reduxSnapshot?.sceneMeshes || []),
    ...(reduxSnapshot?.sceneImages || []),
    ...(reduxSnapshot?.sceneVideos || []),
    ...(reduxSnapshot?.sceneAudios || []),
    ...(reduxSnapshot?.sceneText || []),
    ...(reduxSnapshot?.sceneHotspots || []),
    ...(reduxSnapshot?.allIntersectedObjects || []),
  ];

  const poolHit = pools.find(
    (obj) =>
      obj &&
      (obj.name === selectedObjectId ||
        obj._id === selectedObjectId ||
        obj.id === selectedObjectId ||
        obj.url === selectedObjectId ||
        obj.temp_file_name === selectedObjectId)
  );

  if (poolHit) {
    const live = isLiveSceneObject3D(poolHit)
      ? poolHit
      : resolveLiveSceneObject(
          scene,
          poolHit,
          poolHit.deleteType || poolHit.objType || 'mesh'
        );
    if (live && !isViewportCamera(live)) {
      return clampHistorySelection(scene, live);
    }
  }

  let particleObj = null;
  scene.traverse((obj) => {
    if (particleObj) return;
    if (obj.deleteType === 'particle' && obj.name === selectedObjectId) {
      particleObj = obj;
    }
  });
  if (particleObj) return particleObj;

  return null;
}

function syncEditorSelectionAfterHistoryRestore(reduxSnapshot, scene = gridSceneRef) {
  store.dispatch(setSelectedMeshesForGrouping([]));

  const selectionId = reduxSnapshot?.selectedObjectId;
  const resolved =
    selectionId && scene
      ? resolveSelectObject(scene, selectionId, reduxSnapshot)
      : null;

  if (resolved) {
    store.dispatch(selected_object(resolved));
  } else if (!selectionId) {
    store.dispatch(selected_object(null));
  }

  const selectObj = store.getState().assets_object?.select_object;
  if (!selectObj) {
    store.dispatch(setSelectedLightId(null));
    return;
  }

  if (scene && selectObj?.isObject3D) {
    const clamped = clampHistorySelection(scene, selectObj);
    if (clamped && clamped !== selectObj) {
      store.dispatch(selected_object(clamped));
    }
  }

  const selectedId = getSelectedObjectId(
    store.getState().assets_object?.select_object || selectObj
  );
  if (!selectedId) {
    store.dispatch(setSelectedLightId(null));
    return;
  }

  const lightRecord = (reduxSnapshot?.light_data || []).find(
    (light) =>
      light.id === selectedId ||
      light.name === selectedId ||
      light._id === selectedId ||
      light.temp_file_name === selectedId
  );

  if (lightRecord) {
    store.dispatch(
      setSelectedLightId(lightRecord.id || lightRecord.name || selectedId)
    );
  } else {
    store.dispatch(setSelectedLightId(null));
  }
}

export async function restoreGridHistoryState(snapshot, scene = gridSceneRef) {
  if (!snapshot) return null;

  cancelPendingGridHistoryCommit();
  const releaseSuppress = beginGridHistoryRestore();
  try {
    store.dispatch(restoreGridHistorySnapshot(snapshot.redux));
    store.dispatch(setParticlesHistory(snapshot.particles || []));
    store.dispatch(setAnimationObjectsHistory(snapshot.animationObjects || []));
    store.dispatch(restoreEnvironmentHistory(snapshot.environment || {}));
    store.dispatch(restorePhysicsHistory(snapshot.physics || null));
    restoreSceneGraph(scene, snapshot.sceneGraph);
    await syncSceneAfterGridHistoryRestore(scene);
    syncEditorSelectionAfterHistoryRestore(snapshot.redux, scene);

    return resolveSelectObject(
      scene,
      snapshot.redux?.selectedObjectId,
      snapshot.redux
    );
  } finally {
    endGridHistoryRestore(releaseSuppress);
  }
}

function historyListFingerprint(list = []) {
  return (list || [])
    .map((item) => {
      if (item == null) return '';
      if (typeof item === 'string' || typeof item === 'number') return String(item);
      return String(
        item.uuid ||
          item._id ||
          item.id ||
          item.name ||
          item.url ||
          item.temp_file_name ||
          ''
      );
    })
    .filter(Boolean)
    .join('|');
}

export function gridHistorySnapshotsEqual(a, b) {
  if (!a || !b) return false;
  try {
    // Never JSON.stringify live Object3D refs — that walks parent/children
    // circular graphs and freezes the main thread on every undo commit.
    const toComparable = (snap) => ({
      sceneGraph: snap.sceneGraph,
      redux: {
        assets_data: snap.redux?.assets_data,
        light_data: snap.redux?.light_data,
        camera_properties: snap.redux?.camera_properties,
        events: snap.redux?.events,
        selectedObjectId: snap.redux?.selectedObjectId,
        sceneMeshes: historyListFingerprint(snap.redux?.sceneMeshes),
        sceneHotspots: historyListFingerprint(snap.redux?.sceneHotspots),
        sceneImages: historyListFingerprint(snap.redux?.sceneImages),
        sceneVideos: historyListFingerprint(snap.redux?.sceneVideos),
        sceneAudios: historyListFingerprint(snap.redux?.sceneAudios),
        sceneText: historyListFingerprint(snap.redux?.sceneText),
        sceneElements: historyListFingerprint(snap.redux?.sceneElements),
      },
      particles: snap.particles,
      animationObjects: snap.animationObjects,
      environment: snap.environment,
      physics: snap.physics,
    });
    return (
      JSON.stringify(toComparable(a)) === JSON.stringify(toComparable(b))
    );
  } catch {
    return false;
  }
}

let selectionHandler = null;
let applyingHistorySelection = false;

export function setGridHistorySelectionHandler(handler) {
  selectionHandler = handler;
}

/** True while undo/redo is re-applying selection (skip auto camera-helper show). */
export function isApplyingGridHistorySelection() {
  return applyingHistorySelection;
}

export function applyGridHistorySelection(selected) {
  if (typeof selectionHandler !== 'function') return;
  applyingHistorySelection = true;
  try {
    selectionHandler(selected || null);
  } finally {
    applyingHistorySelection = false;
  }
}

export function withGridHistory(scene, mutateFn, label = 'edit') {
  const targetScene = scene || gridSceneRef;
  if (!targetScene) {
    mutateFn();
    return;
  }

  import('./gridHistorySuppress.js').then(
    ({ isGridHistoryReady, isGridHistorySuppressed, suppressGridHistory }) => {
      if (!isGridHistoryReady() || isGridHistorySuppressed()) {
        mutateFn();
        return;
      }

      import('./gridHistoryDiscrete.js').then(
        ({ captureDiscreteHistoryBefore, recordDiscreteHistoryAfter }) => {
          const before = captureDiscreteHistoryBefore(targetScene);
          if (!before) {
            mutateFn();
            return;
          }

          const endSuppress = suppressGridHistory();
          try {
            mutateFn();
          } finally {
            endSuppress();
            recordDiscreteHistoryAfter(before, targetScene, label);
          }
        }
      );
    }
  );
}
